import { Building2 } from "lucide-react";
import defaultCompanyLogo from "@/assets/default-company-logo.png";
import { OptimizedImage } from "@/components/ui/optimized-image";

interface CompanyLogoProps {
  logoUrl?: string | null;
  companyName: string;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  fallbackIcon?: boolean;
}

const sizeClasses = {
  sm: "w-8 h-8",
  md: "w-12 h-12", 
  lg: "w-16 h-16",
  xl: "w-24 h-24"
};

const iconSizes = {
  sm: "w-4 h-4",
  md: "w-6 h-6",
  lg: "w-8 h-8", 
  xl: "w-12 h-12"
};

export const CompanyLogo = ({ 
  logoUrl, 
  companyName, 
  size = "md", 
  className = "",
  fallbackIcon = false 
}: CompanyLogoProps) => {
  const baseClasses = `${sizeClasses[size]} rounded-lg flex-shrink-0 object-cover ${className}`;
  const dimensions = {
    sm: { width: 32, height: 32 },
    md: { width: 48, height: 48 },
    lg: { width: 64, height: 64 },
    xl: { width: 96, height: 96 }
  };

  // Check if we have a valid logo URL (not placeholder URLs that cause network errors)
  const isValidLogoUrl = logoUrl && 
    !logoUrl.includes('placeholder-logo.com') && 
    !logoUrl.startsWith('http://placeholder') &&
    !logoUrl.startsWith('https://placeholder');

  // If we have a valid logo URL, use it
  if (isValidLogoUrl) {
    return (
      <OptimizedImage
        src={logoUrl}
        alt={`${companyName} logo`}
        width={dimensions[size].width}
        height={dimensions[size].height}
        className={baseClasses}
      />
    );
  }

  // If fallbackIcon is true, show icon instead of default logo
  if (fallbackIcon) {
    return (
      <div className={`${sizeClasses[size]} rounded-lg flex-shrink-0 bg-gradient-primary flex items-center justify-center ${className}`}>
        <Building2 className={`${iconSizes[size]} text-primary-foreground`} />
      </div>
    );
  }

  // Default logo fallback - use regular img tag for static assets
  return (
    <img
      src={defaultCompanyLogo}
      alt={`Default company logo`}
      width={dimensions[size].width}
      height={dimensions[size].height}
      className={baseClasses}
    />
  );
};